import type { ReactNode } from 'react';

/**
 * THE NAV, as data (OO1). The groups are the design's five plus a Tools group for the routes
 * his screenshots never showed - every route that existed before the redesign is still here,
 * and `Layout.nav.test.tsx` fails if one goes missing. Icons are 24-box line icons drawn at
 * 16px (18px on the collapsed rail), stroke 1.8, per the design system: line icons only,
 * never filled, KW red only when the item is active.
 */
export interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
}

export interface NavGroup {
  key: string;
  title: string;
  /** Whether the group starts expanded on a first visit. The group holding the active route
   *  is always shown open regardless, so the operator can see where they are. */
  defaultOpen: boolean;
  items: NavItem[];
}

const I = ({ children }: { children: ReactNode }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'deals',
    title: 'Deals',
    defaultOpen: true,
    items: [
      { path: '/dashboard', label: 'My Deals', icon: (
        <I><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" /></I>
      )},
      { path: '/data-hub', label: 'Property Database', icon: (
        <I><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" /><path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></I>
      )},
      { path: '/tours', label: 'Walk-throughs', icon: (
        <I><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></I>
      )},
      { path: '/threads', label: 'Property Threads', icon: (
        <I><path d="M21 12a8 8 0 01-8 8H8l-5 3 1.5-4.5A8 8 0 1121 12z" /></I>
      )},
      { path: '/portals', label: 'Client Portals', icon: (
        <I><path d="M10 13a5 5 0 007 0l2-2a5 5 0 00-7-7l-1 1" /><path d="M14 11a5 5 0 00-7 0l-2 2a5 5 0 007 7l1-1" /></I>
      )},
      { path: '/change-queue', label: 'Change Queue', icon: (
        <I><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></I>
      )},
    ],
  },
  {
    key: 'crm',
    title: 'CRM',
    defaultOpen: true,
    items: [
      { path: '/crm/contacts', label: 'Contacts', icon: (
        <I><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5" /></I>
      )},
      { path: '/crm/companies', label: 'Companies', icon: (
        <I><rect x="5" y="3" width="14" height="18" rx="1.5" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" /></I>
      )},
      { path: '/crm/deals', label: 'Deals', icon: (
        <I><rect x="6" y="4" width="12" height="17" rx="2" /><path d="M9 4V3h6v1" /></I>
      )},
      // LD1 (spec D10): every live lease and listing Apex brokered, by when to call.
      { path: '/crm/renewals', label: 'Renewals', icon: (
        <I><path d="M4 12a8 8 0 0114-5.3L20 8" /><path d="M20 4v4h-4" /><path d="M20 12a8 8 0 01-14 5.3L4 16" /><path d="M4 20v-4h4" /></I>
      )},
      { path: '/crm/opportunities', label: 'Opportunities', icon: (
        <I><path d="M4 7h16M4 12h16M4 17h10" /></I>
      )},
      { path: '/crm/conversations', label: 'Conversations', icon: (
        <I><path d="M20 11.5a8 8 0 01-11.6 7.1L4 20l1.4-4.2A8 8 0 1120 11.5z" /></I>
      )},
      // LEADS-PAGE: everyone tagged `lead` in GoHighLevel, a tab per property, whose move it is first.
      { path: '/leads', label: 'Leads', icon: (
        <I><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0112 0" /><path d="M16 4l5 5-5 5" /></I>
      )},
      // CAMP1: after a GoHighLevel campaign - who replied, who is hot, who unsubscribed, who to follow up.
      { path: '/campaign-tracker', label: 'Campaign tracker', icon: (
        <I><path d="M4 10v4h3l5 4V6L7 10H4z" /><path d="M16 9a4 4 0 010 6" /><path d="M19 6a8 8 0 010 12" /></I>
      )},
      // MORNING1: who is waiting on him and what he promised - texts, missed calls, to-dos due, deal deadlines.
      { path: '/morning', label: 'Morning', icon: (
        <I><path d="M4 18h16" /><path d="M7 18a5 5 0 0110 0" /><path d="M12 6v3M5.6 9.6l2 2M18.4 9.6l-2 2M3 14h2M19 14h2" /></I>
      )},
      // TODO1: his GoHighLevel tasks from calls (and the unassigned ones), with Done / Drop / Later and the reminders.
      { path: '/todos', label: 'Tasks', icon: (
        <I><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 12l3 3 5-6" /></I>
      )},
      { path: '/crm/needs-attention', label: 'Needs Attention', icon: (
        <I><path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17h.01" /></I>
      )},
    ],
  },
  {
    key: 'marketing',
    title: 'Marketing',
    defaultOpen: true,
    items: [
      // DOC1: "Documents" is the LIBRARY - every document Apex holds, including the 15 that are
      // filed nowhere and appear on no other page. It used to open the GENERATOR, which is the
      // trap `docs/BROWSER-VERIFICATION.md` records: the Documents page offered a generator where
      // he actually looked. The generator keeps its own entry, named for what it does.
      { path: '/documents', label: 'Documents', icon: (
        <I><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><polyline points="14 3 14 8 19 8" /></I>
      )},
      { path: '/documents/generate', label: 'Generate a Document', icon: (
        <I><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><polyline points="14 3 14 8 19 8" /><path d="M9 14h6M12 11v6" /></I>
      )},
      { path: '/prospecting', label: 'Prospecting', icon: (
        <I><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></I>
      )},
      { path: '/zoning-rules', label: 'Zoning Rules', icon: (
        <I><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" /><path d="M9 4v14M15 6v14" /></I>
      )},
      { path: '/campaigns', label: 'Campaigns', icon: (
        <I><rect x="2.5" y="4.5" width="19" height="15" rx="2" /><polyline points="3 6 12 13 21 6" /></I>
      )},
    ],
  },
  {
    key: 'listings',
    title: 'Listings & Assets',
    defaultOpen: false,
    items: [
      // LISTINGS-AI: his listings and the exact words GoHighLevel gives callers about each one.
      { path: '/current-listings', label: 'Current Listings', icon: (
        <I><path d="M5 21V3" /><path d="M5 4h14v9H5" /><path d="M9 8.5h6" /></I>
      )},
      { path: '/listing-sites', label: 'Listing Sites', icon: (
        <I><circle cx="12" cy="12" r="8" /><path d="M4 12h16M12 4c2.5 2.7 2.5 12.3 0 16M12 4c-2.5 2.7-2.5 12.3 0 16" /></I>
      )},
      { path: '/syndication', label: 'Syndication', icon: (
        <I><circle cx="18" cy="5" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="M8.2 10.8l7.6-4.4M8.2 13.2l7.6 4.4" /></I>
      )},
      { path: '/asset-services', label: 'Portfolio', icon: (
        <I><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" /></I>
      )},
      { path: '/asset-services/vendors', label: 'Vendors', icon: (
        <I><rect x="2" y="7" width="12" height="9" /><path d="M14 10h4l3 3v3h-7z" /><circle cx="6" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" /></I>
      )},
    ],
  },
  {
    key: 'intelligence',
    title: 'Intelligence',
    defaultOpen: false,
    items: [
      { path: '/crm-intelligence', label: 'CRM Intel', icon: (
        <I><path d="M13 3L5 14h6l-1 7 8-11h-6z" /></I>
      )},
      { path: '/news-intel', label: 'News Feed', icon: (
        <I><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 9h6M7 13h6M16 9h2M16 13h2" /></I>
      )},
      { path: '/deal-radar', label: 'Deal Radar', icon: (
        <I><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3" /></I>
      ) },
      { path: '/land-upside', label: 'Land Upside', icon: (
        <I><path d="M3 20h18" /><path d="M5 20l5-9 4 6 3-4 3 7" /><circle cx="17" cy="6" r="2" /></I>
      ) },
      { path: '/signal-radar', label: 'Signal Radar', icon: (
        <I><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.5" /></I>
      )},
      { path: '/county-transfers', label: 'Transfers', icon: (
        <I><path d="M4 8h13l-3-3M20 16H7l3 3" /></I>
      )},
      { path: '/permits', label: 'Permits', icon: (
        <I><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><polyline points="14 3 14 8 19 8" /><path d="M9 15l2 2 4-4" /></I>
      )},
      { path: '/street-analysis', label: 'Street AI', icon: (
        <I><rect x="3" y="7" width="12" height="10" rx="2" /><path d="M15 11l6-3v8l-6-3z" /></I>
      )},
      { path: '/prospecting-map', label: 'Map', icon: (
        <I><path d="M12 21s7-6.2 7-11a7 7 0 10-14 0c0 4.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></I>
      )},
      { path: '/comp-search', label: 'Comp Search', icon: (
        <I><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /><path d="M8 11h6M11 8v6" /></I>
      )},
      { path: '/market-intel', label: 'Market Intel', icon: (
        <I><path d="M4 19h16" /><path d="M6 15l4-5 3 3 5-7" /></I>
      )},
      { path: '/data-health', label: 'Data Health', icon: (
        <I><path d="M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z" /><path d="M9 12l2 2 4-4" /></I>
      )},
    ],
  },
  {
    key: 'tools',
    title: 'Tools',
    defaultOpen: false,
    items: [
      { path: '/ai', label: 'AI Conversations', icon: (
        <I><path d="M12 4l1.8 4.7 4.7 1.8-4.7 1.8L12 17l-1.8-4.7-4.7-1.8 4.7-1.8z" /><path d="M19 3v3M17.5 4.5h3" /></I>
      )},
      { path: '/ai/outreach', label: 'Owner Outreach', icon: (
        <I><path d="M16.5 4.5l3 3L9 18H6v-3z" /><path d="M4 21h16" /></I>
      )},
      { path: '/reports', label: 'Reports', icon: (
        <I><rect x="3" y="11" width="4" height="9" /><rect x="10" y="4" width="4" height="16" /><rect x="17" y="8" width="4" height="12" /></I>
      )},
      { path: '/upload', label: 'Upload', icon: (
        <I><path d="M4 16v2a3 3 0 003 3h10a3 3 0 003-3v-2" /><path d="M12 15V4M7 9l5-5 5 5" /></I>
      )},
      { path: '/playbooks', label: 'Playbooks', icon: (
        <I><path d="M4 4h12a3 3 0 013 3v13H7a3 3 0 00-3 3z" /><path d="M4 4v16a3 3 0 013-3h12" /></I>
      )},
      { path: '/ghl-sync', label: 'GHL Sync', icon: (
        <I><path d="M4 4v5h5M20 20v-5h-5" /><path d="M20 9A8 8 0 005.6 6.6L4 9M4 15a8 8 0 0014.4 2.4L20 15" /></I>
      )},
      { path: '/attom', label: 'ATTOM Data', icon: (
        <I><rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" /><path d="M7 7h.01M7 17h.01" /></I>
      )},
    ],
  },
];

/** Every path the nav reaches, for the guard test and the command palette. */
export const NAV_PATHS = NAV_GROUPS.flatMap((g) => g.items.map((i) => i.path));
